import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Toolbar,
  Divider,
  Typography,
  Box,
  Collapse,
  Menu,
  MenuItem,
  Avatar,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  MonetizationOn as MonetizationOnIcon,
  Security as SecurityIcon,
  Tune as SettingIcon,
  ExpandLess,
  ExpandMore,
  AccountCircle,
  DevicesOther as EC2Icon, // EC2 icon
  Edit as ManualUpdateIcon, // Manual Update icon
  Settings as SettingsIcon, // Settings icon
} from "@mui/icons-material";
import LegendToggleIcon from "@mui/icons-material/LegendToggle";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TodayIcon from "@mui/icons-material/Today";
import { useMsal } from "@azure/msal-react";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";

import { useUser } from "../../../functions/userContext";
import { IconMdiJira } from "../../jira/jiraIcon";
import { clearAllCache } from "../../../functions/cache";
import styles from "./sideNav.module.css";
import CookieIcon from "@mui/icons-material/Cookie";
import RobotIcon from "@mui/icons-material/SmartToy";
import { Logout } from "@mui/icons-material";
const SideNav = () => {
  const theme = useTheme();
  const location = useLocation();
  const userMenuRef = useRef(null);
  // State to track expanded sub-menus
  const [openSubMenu, setOpenSubMenu] = useState({
    finance: false,
    infrastructure: false,
    admin: false,
  });

  const { user, ROLE_CONFIG, checkAccess, getFormattedName, getAvatar, profilePicture } = useUser();

  // Toggle a submenu open/close
  const toggleSubMenu = (menu) => {
    //close all submenus
    for (let key in openSubMenu) {
      if (key !== menu) {
        setOpenSubMenu((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    }

    //open/close the selected submenu

    setOpenSubMenu((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const { instance } = useMsal();

  // Helper to check active path
  const isActive = (path) => location.pathname === path;

  const routeConfigs = {
    "/": { showEditButton: true },
  };

  const currentConfig = routeConfigs[location.pathname] || {};

  const fileInputRef = useRef(null);
  const handleClose = () => {
    setUserAnchorEl(null);
  };
  const [userAnchorEl, setUserAnchorEl] = useState(null); // For the user menu
  const logout = () => {
    instance.logoutRedirect(); // Log out with a redirect
    // Or use instance.logoutPopup() for a popup logout
  };
  const handleUserClick = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (userAnchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userAnchorEl]);

  useEffect(() => {
    getAvatar(); // fetch and set avatar in context
  }, [user]);

  if (!user) {
    return <p></p>;
  }

  return (
    <Box className={styles.sideNav}>
      {/* Header with Toggle Button */}

      <Divider />

      <List>
        {/* Overview */}
        <ListItemButton
          component={Link}
          to="/"
          selected={isActive("/")}
          sx={{ backgroundColor: isActive("/") && theme.palette.menu.menuOpen }}
        >
          <ListItemIcon
            sx={{ color: isActive("/") ? theme.palette.warning.main : theme.palette.text.primary }}
          >
            <HomeIcon />
          </ListItemIcon>
        </ListItemButton>
        {/* Finance (With Submenu)  */}
        {checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.finance]) && (
          <ListItemButton title="Costs" component={Link} to="/costs" selected={isActive("/costs")}>
            <ListItemIcon
              sx={{
                color: isActive("/costs") ? theme.palette.warning.main : theme.palette.text.primary,
              }}
            >
              <MonetizationOnIcon />
            </ListItemIcon>
          </ListItemButton>
        )}
        {/* Infrastructure (With Submenu) */}
        <ListItemButton
          title="EC2 Instances"
          component={Link}
          to="/assets/ec2View"
          selected={isActive("/assets/ec2View")}
        >
          <ListItemIcon>
            <EC2Icon
              sx={{
                color: isActive("/assets/ec2View")
                  ? theme.palette.warning.main
                  : theme.palette.text.primary,
              }}
            />
          </ListItemIcon>
        </ListItemButton>

        {/* check access           */}
        {checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.user, ...ROLE_CONFIG.manager]) && (
          <ListItemButton
            title="Security"
            component={Link}
            to="/security/tenable"
            selected={isActive("/security/tenable")}
          >
            <ListItemIcon
              sx={{
                color: isActive("/security/tenable")
                  ? theme.palette.warning.main
                  : theme.palette.text.primary,
              }}
            >
              <SecurityIcon />
            </ListItemIcon>
          </ListItemButton>
        )}

        {/* Monitoring */}
        {checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.user, ...ROLE_CONFIG.editor, ...ROLE_CONFIG.manager]) && (
        <ListItemButton
          title="Monitoring"
          component={Link}
          to="/monitoring"
          selected={isActive("/monitoring")}
        >
          <ListItemIcon
            sx={{
              color: isActive("/monitoring")
                ? theme.palette.warning.main
                : theme.palette.text.primary,
            }}
          >
            <LegendToggleIcon />
          </ListItemIcon>
        </ListItemButton>
        )}

        {checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.manager]) && (
          <ListItemButton
            title="Jira / JSM"
            component={Link}
            to="/jira"
            selected={isActive("/jira")}
          >
            <ListItemIcon
              sx={{
                color: isActive("/jira") ? theme.palette.warning.main : theme.palette.text.primary,
              }}
            >
              <IconMdiJira />
            </ListItemIcon>
          </ListItemButton>
        )}

        {/* Admin (With Submenu) */}
        {checkAccess([...ROLE_CONFIG.admin, ...ROLE_CONFIG.editor]) && (
          <ListItemButton
            title="Admin"
            onClick={() => toggleSubMenu("admin")}
            sx={{
              backgroundColor: openSubMenu.admin ? theme.palette.menu.menuOpen : "transparent",
            }}
          >
            <ListItemIcon
              sx={{
                color: openSubMenu.admin ? theme.palette.warning.main : theme.palette.text.primary,
              }}
            >
              <SettingIcon />
            </ListItemIcon>
          </ListItemButton>
        )}

        <Collapse in={openSubMenu.admin} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              borderTop: "1px solid #818589",
              borderBottom: "1px solid #818589",
            }}
          >
            <ListItemButton
              title="Data Entry"
              component={Link}
              to="/admin/manualUpdate"
              selected={isActive("/admin/manualUpdate")}
            >
              <ListItemIcon>
                <ManualUpdateIcon
                  sx={{
                    color: isActive("/admin/manualUpdate")
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                  }}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              component={Link}
              to="/admin/settings"
              selected={isActive("/admin/settings")}
            >
              <ListItemIcon title="Settings">
                <SettingsIcon
                  sx={{
                    color: isActive("/admin/settings")
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                  }}
                />
              </ListItemIcon>
            </ListItemButton>
          </List>
        </Collapse>

        {/* Reports */}
        {checkAccess([...ROLE_CONFIG.admin]) && (
          <ListItemButton
            title="Reports"
            onClick={() => toggleSubMenu("reports")}
            sx={{
              backgroundColor: openSubMenu.reports ? theme.palette.menu.menuOpen : "transparent",
            }}
          >
            <ListItemIcon
              sx={{
                color: openSubMenu.reports
                  ? theme.palette.warning.main
                  : theme.palette.text.primary,
              }}
            >
              <AssessmentIcon />
            </ListItemIcon>
          </ListItemButton>
        )}

        <Collapse in={openSubMenu.reports} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              borderTop: "1px solid #818589",
              borderBottom: "1px solid #818589",
            }}
          >
            <ListItemButton
              title="Weekly Reports"
              component={Link}
              to="/reports/weeklyReports"
              selected={isActive("/reports/weeklyReports")}
            >
              <ListItemIcon>
                <TodayIcon
                  sx={{
                    color: isActive("/reports/weeklyReports")
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                  }}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              title="MDR Reports"
              component={Link}
              to="/reports/mdrReports"
              selected={isActive("/repots/mdrReports")}
            >
              <ListItemIcon>
                <CalendarMonthIcon
                  sx={{
                    color: isActive("/reports/mdrReports")
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                  }}
                />
              </ListItemIcon>
            </ListItemButton>
            <ListItemButton
              title="AGentic Builder"
              component={Link}
              to="/reports/reportBuilder"
              selected={isActive("/repots/reportBuilder")}
            >
              <ListItemIcon>
                <RobotIcon
                  sx={{
                    color: isActive("/reports/reportBuilder")
                      ? theme.palette.warning.main
                      : theme.palette.text.primary,
                  }}
                />
              </ListItemIcon>
            </ListItemButton>
          </List>
        </Collapse>
      </List>

      <Box className={styles.userMenuBox}>
        <IconButton onClick={handleUserClick} color="inherit">
          {profilePicture ? (
            <Avatar
              src={profilePicture}
              sx={{ width: 40, height: 40, marginLeft: "-4px", marginBottom: "-10px" }}
            />
          ) : (
            <AccountCircle
              sx={{ width: 40, height: 40, marginLeft: "-4px", marginBottom: "-10px" }}
              fontSize="large"
            />
          )}
        </IconButton>

        <Menu
          className={styles.userMenuWindow}
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={handleClose}
        >
          {user && (
            <>
              <MenuItem sx={{ borderBottom: "1px solid #aaa", padding: "10px 20px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "5px",
                    marginRight: "15px",
                  }}
                >
                  {profilePicture ? (
                    <Avatar
                      sx={{ width: 60, height: 60, marginLeft: "-4px", marginBottom: "10px" }}
                      src={profilePicture}
                    />
                  ) : (
                    <AccountCircle
                      sx={{ width: 60, height: 60, marginLeft: "-4px", marginBottom: "10px" }}
                      fontSize="large"
                    />
                  )}
                </Box>
                <Box sx={{ marginTop: "-50px" }}>
                  <Typography variant="body1">{getFormattedName()}</Typography>
                  <Typography variant="body2">{user.jobTitle}</Typography>
                  <Typography variant="body2">{user.department}</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                sx={{ marginTop: "10px" }}
                onClick={async () => {
                  await clearAllCache();
                  window.location.reload();
                  handleClose();
                }}
              >
                <CookieIcon sx={{ mr: 1 }} /> Clear Local Cache
              </MenuItem>
              <MenuItem onClick={logout} sx={{ marginBottom: "10px" }}>
                <Logout sx={{ mr: 1 }} /> Logout
              </MenuItem>
              <Box>
                <Typography variant="caption" className={styles.version}>
                  ver beta 2
                </Typography>
              </Box>
            </>
          )}

          {!user && (
            <MenuItem onClick={logout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

export default SideNav;
